/**
 * Christmas decoration ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasDecorationIdeasPack: ToilePack = {
    slug: 'christmas-decoration-ideas',
    category: 'season',
    name: 'Christmas decoration ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'living', label: 'Living room', subject: 'a living room with a tree, garland and stockings'},
        {key: 'stairs', label: 'Staircase', subject: 'a staircase with garland and ribbons'},
        {key: 'window', label: 'Window', subject: 'a window with lights and paper snowflakes'},
    ],
    styles: [
        {key: 'classic', label: 'Classic red', style: 'traditional Christmas decor in soft afternoon daylight from a window camera-left, a warm balance of bright windows and soft shadow', palette: 'red and green with brass and warm wood', framing: 'eye-level wide shot of the whole space'},
        {key: 'nordic', label: 'Nordic white', style: 'Scandinavian minimal decor in pale wintry daylight from camera-right, a pale airy balance with soft gray shadows', palette: 'white, natural wood, eucalyptus green and linen', framing: 'eye-level, generous calm space around the decor'},
        {key: 'dusk', label: 'Evening glow', style: 'the decor at dusk lit by the warm glow of fairy lights and candles, blue twilight falling from behind through the glass, a low balance of deep shadow and golden pools', palette: 'gold and amber against deep blue dusk', framing: 'slightly low wide shot'},
    ],
    directions: ['pink christmas', 'small apartment', 'front porch'],
    directionHint: 'try: pink christmas',
    seo: {
        title: 'Christmas Decoration Ideas: Living Room, Staircase, Window',
        metaDescription: 'Nine photos of Christmas home decor in a living room, up a staircase and at a window, in classic red, Nordic white and evening glow.',
        h1: 'Christmas decoration ideas: living room, staircase, window',
        tagline: 'Three rooms, each dressed in classic red, Nordic white and evening glow.',
        intro: 'Whole-room Christmas decor for a house: a living room with a tree, garland and stockings, a staircase wound with garland and ribbon, and a window with lights and paper snowflakes. Each room comes in a classic red and green look, a pale Nordic white look and an evening look lit by fairy lights and candles. Type a twist, such as pink Christmas or small apartment, and the room is asked to take it on.',
        useCases: [
            'a whole-house idea board for the family',
            'a plan for one room before guests arrive',
            'a shopping list for garland, ribbon and lights',
            'a mood board for a holiday party',
        ],
        faq: [
            {q: 'Which rooms are shown?', a: 'A living room, a staircase and a window. Each appears in three decor styles, so the nine pictures are three rooms in three looks.'},
            {q: 'What is the difference between the styles?', a: 'Classic red uses red, green, brass and warm wood in daylight. Nordic white uses white, natural wood and eucalyptus in pale light. Evening glow is lit by fairy lights and candles at dusk.'},
            {q: 'Can I ask for a different look?', a: 'Yes. Type a twist, such as pink Christmas or front porch, and the page asks for a version that matches it. The nine pictures are the samples shown; a typed twist is a request.'},
        ],
    },
}
